/**
 * Renders a testimonial as a 1080×1920 Instagram Story PNG via canvas.
 * Returns a Promise<Blob>.
 */
export async function renderStoryCard(t) {
  const W = 1080;
  const H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Gradient background — dawn celestial
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#faf8f5");
  bg.addColorStop(0.45, "#f5d5b8");
  bg.addColorStop(1, "#d8d4ec");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Soft organic blobs
  const blob = (x, y, r, color, alpha) => {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.filter = "blur(80px)";
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };
  blob(180, 320, 280, "#f2ccb7", 0.7);
  blob(900, 1500, 320, "#d8d4ec", 0.65);
  blob(540, 1000, 220, "#84a59d", 0.35);

  // Frame
  ctx.strokeStyle = "rgba(40,75,99,0.18)";
  ctx.lineWidth = 2;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  // Top brand
  ctx.fillStyle = "#5d7d76";
  ctx.font = "600 28px 'Plus Jakarta Sans', sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("· JUST BE YOU · BY MEHJABIN ·", W / 2, 160);

  // Quote mark
  ctx.fillStyle = "#e8a888";
  ctx.font = "italic 240px 'Fraunces', Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("\u201C", W / 2, 380);

  // Quote text — wrap to width 880
  ctx.fillStyle = "#1a2530";
  ctx.font = "italic 500 56px 'Fraunces', Georgia, serif";
  ctx.textAlign = "center";
  const maxWidth = 880;
  const words = (t.quote || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const lineHeight = 78;
  const total = lines.length * lineHeight;
  let y = (H - total) / 2 - 40;
  for (const l of lines) {
    ctx.fillText(l, W / 2, y);
    y += lineHeight;
  }

  // Stars
  const rating = Math.max(1, Math.min(5, t.rating || 5));
  ctx.fillStyle = "#d4a574";
  ctx.font = "60px sans-serif";
  const starsRow = "★".repeat(rating) + "☆".repeat(5 - rating);
  ctx.fillText(starsRow, W / 2, y + 90);

  // Name + meta
  ctx.fillStyle = "#284b63";
  ctx.font = "italic 500 44px 'Fraunces', Georgia, serif";
  ctx.fillText("— " + (t.name || "Anonymous"), W / 2, y + 200);

  const meta = [t.role, t.location].filter(Boolean).join(" · ");
  if (meta) {
    ctx.fillStyle = "#5d7d76";
    ctx.font = "500 28px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(meta, W / 2, y + 250);
  }

  // Footer
  ctx.fillStyle = "rgba(26,37,48,0.55)";
  ctx.font = "600 24px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText("Just be you — read more, book a session", W / 2, H - 130);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
